import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/Layout';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Users, 
  Calendar,
  RefreshCw,
  Download,
  Clock,
  Target,
  Zap,
  ShoppingCart,
  CheckCircle,
  Filter,
  BarChart3,
  Loader2,
  AlertCircle,
  Plus,
  GripVertical,
  ChevronDown,
  Percent,
  Activity,
  PieChart,
  TrendingUp as TrendUp,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import ReportBuilderModal from '../../components/marketing/ReportBuilderModal';
import MarketingDashboardGrid from '../../components/marketing/MarketingDashboardGrid';
import AdsSummaryCard from '../../components/marketing/AdsSummaryCard';
import AdSpendTracker from '../../components/marketing/AdSpendTracker';

const API = process.env.REACT_APP_BACKEND_URL;

// All available metrics that can be shown in Key Metrics section
const ALL_METRICS = [
  { id: 'total_leads', title: 'Total Leads', icon: Users, color: 'blue', category: 'volume' },
  { id: 'schedules', title: 'Schedules / Appointments', icon: Calendar, color: 'purple', category: 'volume' },
  { id: 'purchases', title: 'Purchases', icon: ShoppingCart, color: 'green', category: 'volume' },
  { id: 'attributed_revenue', title: 'Attributed Revenue', icon: DollarSign, color: 'orange', category: 'revenue' },
  { id: 'total_conversions', title: 'Total Conversions', icon: Target, color: 'cyan', category: 'volume' },
  { id: 'lead_schedule_rate', title: 'Lead → Appt Rate', icon: Zap, color: 'pink', category: 'rates' },
  { id: 'schedule_purchase_rate', title: 'Demo → Sale Rate', icon: CheckCircle, color: 'green', category: 'rates' },
  { id: 'avg_deal_value', title: 'Avg Deal Value', icon: Wallet, color: 'blue', category: 'revenue' },
  { id: 'cost_per_lead', title: 'Cost Per Lead', icon: Activity, color: 'orange', category: 'efficiency' },
  { id: 'cost_per_acquisition', title: 'Cost Per Acquisition', icon: PieChart, color: 'purple', category: 'efficiency' },
  { id: 'roas', title: 'ROAS', icon: TrendUp, color: 'green', category: 'efficiency' },
  { id: 'close_rate', title: 'Close Rate', icon: Percent, color: 'cyan', category: 'rates' },
];

// Default visible metrics
const DEFAULT_VISIBLE_METRICS = [
  'total_leads', 'schedules', 'purchases', 'attributed_revenue',
  'total_conversions', 'lead_schedule_rate', 'schedule_purchase_rate'
];

// Date range options
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7', label: 'Last 7 Days' },
  { value: 'last_14', label: 'Last 14 Days' },
  { value: 'last_30', label: 'Last 30 Days' },
  { value: 'last_90', label: 'Last 90 Days' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'ytd', label: 'Year to Date' },
];

// Map short date range values to full format for reports API
const mapDateRangeForReports = (shortValue) => {
  const mapping = {
    'last_7': 'last_7_days',
    'last_14': 'last_14_days',
    'last_30': 'last_30_days',
    'last_90': 'last_90_days',
  };
  return mapping[shortValue] || shortValue;
};

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue', status, onRemove, metricId }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-green-600',
    purple: 'from-purple-500 to-violet-600',
    orange: 'from-orange-500 to-amber-600',
    pink: 'from-pink-500 to-rose-600',
    cyan: 'from-cyan-500 to-teal-600',
  };
  
  const statusColors = {
    green: 'bg-emerald-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
  };

  return (
    <Card className="relative overflow-hidden group" data-testid={`kpi-card-${metricId}`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses[color]} opacity-5`} />
      {/* Remove button - appears on hover */}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(metricId);
          }}
          className="absolute top-2 right-2 z-10 p-1 rounded-full bg-muted/80 hover:bg-destructive hover:text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove metric"
          data-testid={`remove-metric-${metricId}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      )}
      <CardContent className="pt-6 relative">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-2xl font-bold">{value}</p>
              {status && (
                <span className={`w-2 h-2 rounded-full ${statusColors[status] || 'bg-gray-400'}`} title={`Status: ${status}`} />
              )}
            </div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
        {trend !== undefined && (
          <div className="flex items-center mt-3 text-xs">
            {trend >= 0 ? (
              <TrendingUp className="h-3 w-3 text-emerald-500 mr-1" />
            ) : (
              <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
            )}
            <span className={trend >= 0 ? 'text-emerald-500' : 'text-red-500'}>
              {Math.abs(trend)}% {trendValue || 'vs prior period'}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Marketing Tracker Component
const MarketingTracker = () => {
  const { user, isAdmin: authIsAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dateRange, setDateRange] = useState('last_30');
  const [groupBy, setGroupBy] = useState('configured_name');
  
  // Data states
  const [overview, setOverview] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [filters, setFilters] = useState(null);
  const [status, setStatus] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [error, setError] = useState(null);
  
  // Realtime pacing data
  const [realtimePacingData, setRealtimePacingData] = useState(null);
  const [realtimePacingLoading, setRealtimePacingLoading] = useState(true);

  // Filter states
  const [selectedConfiguredName, setSelectedConfiguredName] = useState('all');
  
  // Report Builder states
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  
  // Section ordering state (persisted to localStorage)
  const [sectionOrder, setSectionOrder] = useState(() => {
    const saved = localStorage.getItem('marketingTrackerSectionOrder');
    // Filter out 'targets' if it exists in saved state
    const order = saved ? JSON.parse(saved) : ['metrics'];
    return order.filter(id => id !== 'targets');
  });
  
  // Visible metrics state (persisted to localStorage)
  const [visibleMetrics, setVisibleMetrics] = useState(() => {
    const saved = localStorage.getItem('marketingTrackerVisibleMetrics');
    return saved ? JSON.parse(saved) : DEFAULT_VISIBLE_METRICS;
  });
  
  // Drag state for section reordering
  const [draggedSection, setDraggedSection] = useState(null);
  
  // Save section order to localStorage
  useEffect(() => {
    localStorage.setItem('marketingTrackerSectionOrder', JSON.stringify(sectionOrder));
  }, [sectionOrder]);
  
  // Save visible metrics to localStorage
  useEffect(() => {
    localStorage.setItem('marketingTrackerVisibleMetrics', JSON.stringify(visibleMetrics));
  }, [visibleMetrics]);
  
  // Handle section drag start
  const handleSectionDragStart = (e, sectionId) => {
    setDraggedSection(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  // Handle section drag over
  const handleSectionDragOver = (e, targetId) => {
    e.preventDefault();
    if (draggedSection && draggedSection !== targetId) {
      setSectionOrder(prev => {
        const newOrder = [...prev];
        const draggedIdx = newOrder.indexOf(draggedSection);
        const targetIdx = newOrder.indexOf(targetId);
        newOrder.splice(draggedIdx, 1);
        newOrder.splice(targetIdx, 0, draggedSection);
        return newOrder;
      });
    }
  };
  
  // Handle section drag end
  const handleSectionDragEnd = () => {
    setDraggedSection(null);
  };
  
  // Toggle metric visibility
  const toggleMetric = (metricId) => {
    setVisibleMetrics(prev => {
      if (prev.includes(metricId)) {
        return prev.filter(id => id !== metricId);
      } else {
        return [...prev, metricId];
      }
    });
  };
  
  // Get metric value from realtime pacing data (primary) or overview (fallback)
  const getMetricValue = (metricId) => {
    // Map frontend metric IDs to backend API metric keys
    const pacingMetricMap = {
      'total_leads': 'leads',
      'schedules': 'appointments',
      'purchases': 'sales',
      'attributed_revenue': 'revenue',
      'total_conversions': null, // calculated
      'lead_schedule_rate': 'set_rate',
      'schedule_purchase_rate': 'close_rate',
      'avg_deal_value': 'average_ticket',
      'cost_per_lead': 'cpl',
      'cost_per_acquisition': 'cpa',
      'roas': 'roas',
      'close_rate': 'close_rate',
    };
    
    // Try realtime pacing data first (Builder Prime source of truth)
    const backendKey = pacingMetricMap[metricId];
    const pacingMetrics = realtimePacingData?.metrics;
    
    if (pacingMetrics && backendKey && pacingMetrics[backendKey]) {
      const metric = pacingMetrics[backendKey];
      const actual = metric.actual;
      
      // Format based on metric type
      switch (metricId) {
        case 'total_leads':
        case 'schedules':
        case 'purchases':
          return actual?.toLocaleString() || '0';
        case 'attributed_revenue':
          return `$${(actual || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        case 'avg_deal_value':
          return `$${(actual || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        case 'lead_schedule_rate':
        case 'schedule_purchase_rate':
        case 'close_rate':
          return `${(actual || 0).toFixed(1)}%`;
        case 'cost_per_lead':
        case 'cost_per_acquisition':
          return actual > 0 ? `$${actual.toFixed(2)}` : 'N/A';
        case 'roas':
          return actual > 0 ? `${actual.toFixed(2)}x` : 'N/A';
        default:
          return actual?.toLocaleString() || '0';
      }
    }
    
    // Handle calculated metrics
    if (metricId === 'total_conversions' && pacingMetrics) {
      const leads = pacingMetrics.leads?.actual || 0;
      const appointments = pacingMetrics.appointments?.actual || 0;
      const sales = pacingMetrics.sales?.actual || 0;
      return (leads + appointments + sales).toLocaleString();
    }
    
    // Fallback to Cometly overview data
    if (!overview) return '0';
    
    switch (metricId) {
      case 'total_leads':
        return overview.leads?.toLocaleString() || '0';
      case 'schedules':
        return overview.schedules?.toLocaleString() || '0';
      case 'purchases':
        return overview.purchases?.toLocaleString() || '0';
      case 'attributed_revenue':
        return `$${(overview.attributed_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      case 'total_conversions':
        return ((overview.leads || 0) + (overview.schedules || 0) + (overview.purchases || 0)).toLocaleString();
      case 'lead_schedule_rate':
        return overview.leads > 0 ? `${((overview.schedules / overview.leads) * 100).toFixed(1)}%` : '0%';
      case 'schedule_purchase_rate':
        return overview.schedules > 0 ? `${((overview.purchases / overview.schedules) * 100).toFixed(1)}%` : '0%';
      case 'avg_deal_value':
        return overview.purchases > 0 ? `$${(overview.attributed_revenue / overview.purchases).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '$0';
      case 'cost_per_lead':
        return overview.ad_spend && overview.leads > 0 ? `$${(overview.ad_spend / overview.leads).toFixed(2)}` : 'N/A';
      case 'cost_per_acquisition':
        return overview.ad_spend && overview.purchases > 0 ? `$${(overview.ad_spend / overview.purchases).toFixed(2)}` : 'N/A';
      case 'roas':
        return overview.ad_spend > 0 ? `${(overview.attributed_revenue / overview.ad_spend).toFixed(2)}x` : 'N/A';
      case 'close_rate':
        return overview.leads > 0 ? `${((overview.purchases / overview.leads) * 100).toFixed(1)}%` : '0%';
      default:
        return '0';
    }
  };
  
  // Get metric status (green/yellow/red) from realtime pacing data
  const getMetricStatus = (metricId) => {
    const pacingMetricMap = {
      'total_leads': 'leads',
      'schedules': 'appointments',
      'purchases': 'sales',
      'attributed_revenue': 'revenue',
      'lead_schedule_rate': 'set_rate',
      'schedule_purchase_rate': 'close_rate',
      'avg_deal_value': 'average_ticket',
      'cost_per_lead': 'cpl',
      'cost_per_acquisition': 'cpa',
      'roas': 'roas',
      'close_rate': 'close_rate',
    };
    
    // Rate metrics should calculate status based on actual/target, not pace projection
    const isRateMetric = ['lead_schedule_rate', 'schedule_purchase_rate', 'close_rate'].includes(metricId);
    
    const backendKey = pacingMetricMap[metricId];
    const pacingMetrics = realtimePacingData?.metrics;
    
    if (pacingMetrics && backendKey && pacingMetrics[backendKey]) {
      if (isRateMetric) {
        // For rate metrics: calculate status from actual vs target
        const actual = pacingMetrics[backendKey].actual || 0;
        const target = pacingMetrics[backendKey].target || 0;
        if (target > 0) {
          const pctOfTarget = (actual / target) * 100;
          if (pctOfTarget >= 95) return 'green';
          if (pctOfTarget >= 80) return 'yellow';
          return 'red';
        }
      }
      return pacingMetrics[backendKey].status;
    }
    return null;
  };
  
  // Get metric subtitle with pacing info
  const getMetricSubtitle = (metricId) => {
    const pacingMetricMap = {
      'total_leads': 'leads',
      'schedules': 'appointments',
      'purchases': 'sales',
      'attributed_revenue': 'revenue',
      'avg_deal_value': 'average_ticket',
      'cost_per_lead': 'cpl',
      'cost_per_acquisition': 'cpa',
      'roas': 'roas',
      'close_rate': 'close_rate',
      'lead_schedule_rate': 'set_rate',
      'schedule_purchase_rate': 'close_rate',
    };
    
    // Rate metrics should NOT use pace projection - use actual/target directly
    const isRateMetric = ['lead_schedule_rate', 'schedule_purchase_rate', 'close_rate'].includes(metricId);
    
    const backendKey = pacingMetricMap[metricId];
    const pacingMetrics = realtimePacingData?.metrics;
    
    // Show pacing info if available
    if (pacingMetrics && backendKey && pacingMetrics[backendKey]) {
      const metric = pacingMetrics[backendKey];
      
      if (isRateMetric) {
        // For rate metrics: show actual vs target directly (no pace projection)
        const actual = metric.actual || 0;
        const target = metric.target || 0;
        if (target > 0) {
          const pctOfTarget = (actual / target) * 100;
          return `${pctOfTarget.toFixed(0)}% of target (${target.toFixed(1)}%)`;
        }
      } else {
        // For volume metrics: use the pacing percent_of_target
        const pctOfTarget = metric.percent_of_target;
        if (pctOfTarget && pctOfTarget > 0) {
          return `${pctOfTarget.toFixed(0)}% of target`;
        }
      }
    }
    
    // Default subtitles
    switch (metricId) {
      case 'total_leads':
        return realtimePacingData?.active_month_name || 'MTD';
      case 'schedules':
        return 'Appointments set';
      case 'purchases':
        return 'Jobs sold';
      case 'attributed_revenue':
        return 'Total contracted';
      case 'total_conversions':
        return 'Leads + Appts + Sales';
      case 'lead_schedule_rate':
        return 'Lead → Appointment';
      case 'schedule_purchase_rate':
        return 'Demo → Sale';
      case 'avg_deal_value':
        return 'Per job sold';
      case 'cost_per_lead':
        return 'Ad spend / leads';
      case 'cost_per_acquisition':
        return 'Ad spend / sales';
      case 'roas':
        return 'Revenue / spend';
      case 'close_rate':
        return 'Demos → Sales';
      default:
        return '';
    }
  };
  
  // Handler to remove a metric from visible list
  const handleRemoveMetric = (metricId) => {
    setVisibleMetrics(prev => prev.filter(id => id !== metricId));
    toast.success(`Removed "${ALL_METRICS.find(m => m.id === metricId)?.title || metricId}" from view`);
  };
  
  // Map dateRange filter value to pacing API period format
  const mapDateRangeToPacingPeriod = (range) => {
    const mapping = {
      'today': 'week',
      'yesterday': 'week',
      'last_7': 'week',
      'last_14': 'mtd',
      'last_30': 'mtd',
      'last_90': 'qtd',
      'this_month': 'mtd',
      'last_month': 'mtd',
      'this_quarter': 'qtd',
      'this_year': 'ytd',
      'ytd': 'ytd',
    };
    return mapping[range] || 'mtd';
  };
  
  // Get display label for current period
  const getPeriodDisplayLabel = (range) => {
    const labels = {
      'today': 'Today',
      'yesterday': 'Yesterday',
      'last_7': 'Last 7 Days',
      'last_14': 'Last 14 Days',
      'last_30': 'Last 30 Days',
      'last_90': 'Last 90 Days',
      'this_month': 'This Month',
      'last_month': 'Last Month',
      'this_quarter': 'This Quarter',
      'this_year': 'This Year',
      'ytd': 'Year to Date',
    };
    return labels[range] || 'MTD';
  };
  
  // Fetch realtime pacing data
  const fetchRealtimePacing = useCallback(async () => {
    setRealtimePacingLoading(true);
    try {
      const period = mapDateRangeToPacingPeriod(dateRange);
      const response = await axios.get(`${API}/api/marketing/pacing/realtime?period=${period}`);
      setRealtimePacingData(response.data);
    } catch (err) {
      console.error('Error fetching realtime pacing:', err);
      // Silent fail - section will show empty state
    } finally {
      setRealtimePacingLoading(false);
    }
  }, [dateRange]);

  // Fetch user's reports
  const fetchReports = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/marketing/reports`);
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  }, []);

  // Fetch overview metrics
  const fetchOverview = useCallback(async () => {
    try {
      const params = new URLSearchParams({ date_range: dateRange });
      if (selectedConfiguredName && selectedConfiguredName !== 'all') {
        params.append('configured_name', selectedConfiguredName);
      }
      
      const response = await axios.get(`${API}/api/marketing/tracker/overview?${params}`);
      setOverview(response.data);
    } catch (err) {
      console.error('Error fetching overview:', err);
      setError('Failed to load overview metrics');
    }
  }, [dateRange, selectedConfiguredName]);

  // Fetch breakdown data
  const fetchBreakdown = useCallback(async () => {
    try {
      const params = new URLSearchParams({ 
        date_range: dateRange,
        group_by: groupBy,
        limit: '100'
      });
      if (selectedConfiguredName && selectedConfiguredName !== 'all') {
        params.append('configured_name', selectedConfiguredName);
      }
      
      const response = await axios.get(`${API}/api/marketing/tracker/breakdown?${params}`);
      setBreakdown(response.data);
    } catch (err) {
      console.error('Error fetching breakdown:', err);
    }
  }, [dateRange, groupBy, selectedConfiguredName]);

  // Fetch filters
  const fetchFilters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/marketing/tracker/filters`);
      setFilters(response.data);
    } catch (err) {
      console.error('Error fetching filters:', err);
    }
  }, []);

  // Fetch sync status (basic)
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/marketing/tracker/status`);
      setStatus(response.data);
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  }, []);

  // Fetch scheduler status (detailed - last_success_at, last_error, etc.)
  const fetchSchedulerStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/marketing/tracker/cometly/status`);
      setSchedulerStatus(response.data);
    } catch (err) {
      console.error('Error fetching scheduler status:', err);
    }
  }, []);

  // Trigger manual sync
  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/api/marketing/tracker/sync`);
      toast.success('Sync started in background');
      // Refresh data after a delay to allow sync to complete
      setTimeout(() => {
        fetchOverview();
        fetchBreakdown();
        fetchStatus();
        fetchSchedulerStatus();
      }, 5000);
    } catch (err) {
      toast.error('Failed to start sync');
    } finally {
      setSyncing(false);
    }
  };

  // Export to CSV
  const handleExport = () => {
    if (!breakdown.length) return;
    
    const headers = ['Name', 'Event Type', 'Count', 'Revenue', 'Avg Amount'];
    const rows = breakdown.map(row => [
      row.name,
      row.event_name || '',
      row.count,
      row.revenue.toFixed(2),
      row.avg_amount.toFixed(2)
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marketing-tracker-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Data exported to CSV');
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([
        fetchOverview(),
        fetchBreakdown(),
        fetchFilters(),
        fetchStatus(),
        fetchSchedulerStatus(),
        fetchReports(),
        fetchRealtimePacing()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchOverview, fetchBreakdown, fetchFilters, fetchStatus, fetchSchedulerStatus, fetchReports]);

  // Handle report created/updated
  const handleReportCreated = (report, addedToPage) => {
    fetchReports();
    if (addedToPage) {
      setDashboardRefreshKey(prev => prev + 1);
    }
  };

  // Handle edit report
  const handleEditReport = (report) => {
    setEditingReport(report);
    setShowReportBuilder(true);
  };

  // Handle duplicate report
  const handleDuplicateReport = async (report) => {
    try {
      await axios.post(`${API}/api/marketing/reports/${report.id}/duplicate`);
      toast.success('Report duplicated');
      fetchReports();
      setDashboardRefreshKey(prev => prev + 1);
    } catch (err) {
      toast.error('Failed to duplicate report');
    }
  };

  // Check if user is admin - use context value + enhanced fallback check
  const isAdmin = useMemo(() => {
    // First use the context value
    if (authIsAdmin) return true;
    
    // Fallback check with multiple possible role locations
    const role = user?.role || user?.userRole || '';
    const email = user?.email || '';
    
    // Super admin email override
    const isSuperAdminEmail = email.toLowerCase() === 'rmarch@rhinoshield.com';
    
    const adminCheck = ['super_admin', 'admin'].includes(role) || isSuperAdminEmail;
    
    // Always log for debugging this persistent issue
    console.log('[MarketingTracker] User auth check:', { 
      authIsAdmin,
      role, 
      email: email.substring(0, 10) + '...', 
      isAdmin: adminCheck,
      userObject: user ? 'exists' : 'null'
    });
    
    return adminCheck;
  }, [user, authIsAdmin]);

  // Format last sync time - use scheduler status for most accurate info
  const lastSyncFormatted = useMemo(() => {
    // Prefer scheduler status for last_success_at (more accurate)
    const lastSuccess = schedulerStatus?.last_success_at || status?.last_sync_at;
    if (!lastSuccess) return 'Never';
    const date = new Date(lastSuccess);
    return date.toLocaleString();
  }, [schedulerStatus?.last_success_at, status?.last_sync_at]);

  // Get sync status indicator (success/error)
  const syncStatusInfo = useMemo(() => {
    if (!schedulerStatus) return { status: 'unknown', color: 'gray' };
    
    if (schedulerStatus.last_error && !schedulerStatus.last_success_at) {
      return { status: 'error', color: 'red', message: schedulerStatus.last_error };
    }
    
    if (schedulerStatus.last_success_at) {
      return { status: 'success', color: 'green' };
    }
    
    return { status: 'pending', color: 'yellow' };
  }, [schedulerStatus]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
            <p className="mt-2 text-sm text-muted-foreground">Loading Cometly data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error && !overview) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-3" />
            <p className="text-lg font-medium">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Retry
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-6" data-testid="marketing-tracker-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold">Marketing Tracker</h1>
              <p className="text-sm text-muted-foreground">
                Real-time conversion analytics from Cometly
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Sync Status - Enhanced with success/error indicator */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-lg" data-testid="sync-status-display">
            {/* Status indicator dot */}
            <div className={`w-2 h-2 rounded-full ${
              syncStatusInfo.status === 'success' ? 'bg-green-500' :
              syncStatusInfo.status === 'error' ? 'bg-red-500' :
              syncStatusInfo.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-400'
            }`} />
            <Clock className="h-3 w-3" />
            <span>Last Sync: {lastSyncFormatted}</span>
            {schedulerStatus?.rows_upserted > 0 && (
              <span className="text-green-600">
                (+{schedulerStatus.rows_upserted} rows)
              </span>
            )}
            {status?.total_events > 0 && (
              <Badge variant="outline" className="ml-1">
                {status.total_events.toLocaleString()} events
              </Badge>
            )}
            {schedulerStatus?.is_running && (
              <Badge variant="secondary" className="ml-1 text-[10px]">
                Auto-sync active
              </Badge>
            )}
          </div>

          {/* Show error if last sync failed */}
          {syncStatusInfo.status === 'error' && syncStatusInfo.message && (
            <div className="text-xs text-red-500 max-w-[200px] truncate" title={syncStatusInfo.message}>
              Error: {syncStatusInfo.message}
            </div>
          )}

          {/* Sync Button (Admin only) */}
          {isAdmin && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleSync}
              disabled={syncing}
              data-testid="sync-now-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          )}

          {/* Export Button */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleExport}
            disabled={!breakdown.length}
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filters</span>
          </div>
          
          {/* Date Range */}
          <div className="flex items-center gap-2">
            <Label className="text-xs">Date Range</Label>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[150px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map(range => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Event Type Filter */}
          <div className="flex items-center gap-2">
            <Label className="text-xs">Event Type</Label>
            <Select value={selectedConfiguredName} onValueChange={setSelectedConfiguredName}>
              <SelectTrigger className="w-[180px] h-8">
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {filters?.configured_names?.map(name => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Group By */}
          <div className="flex items-center gap-2">
            <Label className="text-xs">Group By</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[150px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="configured_name">Event Type</SelectItem>
                <SelectItem value="event_name">Raw Event</SelectItem>
                <SelectItem value="date">Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Apply button */}
          <Button 
            size="sm" 
            onClick={() => { fetchOverview(); fetchBreakdown(); fetchRealtimePacing(); }}
            data-testid="apply-filters-btn"
          >
            Apply
          </Button>
        </div>
      </Card>

      {/* Reorderable Sections */}
      {sectionOrder.map((sectionId) => {
        if (sectionId === 'metrics') {
          const metricsToShow = ALL_METRICS.filter(m => visibleMetrics.includes(m.id));
          
          return (
            <Card 
              key="metrics"
              className={`border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent transition-all ${
                draggedSection === 'metrics' ? 'opacity-50 scale-[0.98]' : ''
              }`}
              draggable
              onDragStart={(e) => handleSectionDragStart(e, 'metrics')}
              onDragOver={(e) => handleSectionDragOver(e, 'metrics')}
              onDragEnd={handleSectionDragEnd}
              data-testid="metrics-section"
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div 
                      className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
                      title="Drag to reorder"
                    >
                      <GripVertical className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        Key Metrics
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/50 px-2 py-0.5 rounded">
                          📈 {getPeriodDisplayLabel(dateRange)}
                        </span>
                      </CardTitle>
                      <CardDescription>What you&apos;ve achieved so far (from Builder Prime)</CardDescription>
                    </div>
                  </div>
                  
                  {/* Add Metrics Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="add-metrics-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Metrics
                        <ChevronDown className="h-4 w-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>Volume Metrics</DropdownMenuLabel>
                      {ALL_METRICS.filter(m => m.category === 'volume').map(metric => (
                        <DropdownMenuCheckboxItem
                          key={metric.id}
                          checked={visibleMetrics.includes(metric.id)}
                          onCheckedChange={() => toggleMetric(metric.id)}
                        >
                          <metric.icon className="h-4 w-4 mr-2" />
                          {metric.title}
                        </DropdownMenuCheckboxItem>
                      ))}
                      
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Revenue Metrics</DropdownMenuLabel>
                      {ALL_METRICS.filter(m => m.category === 'revenue').map(metric => (
                        <DropdownMenuCheckboxItem
                          key={metric.id}
                          checked={visibleMetrics.includes(metric.id)}
                          onCheckedChange={() => toggleMetric(metric.id)}
                        >
                          <metric.icon className="h-4 w-4 mr-2" />
                          {metric.title}
                        </DropdownMenuCheckboxItem>
                      ))}
                      
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Rate Metrics</DropdownMenuLabel>
                      {ALL_METRICS.filter(m => m.category === 'rates').map(metric => (
                        <DropdownMenuCheckboxItem
                          key={metric.id}
                          checked={visibleMetrics.includes(metric.id)}
                          onCheckedChange={() => toggleMetric(metric.id)}
                        >
                          <metric.icon className="h-4 w-4 mr-2" />
                          {metric.title}
                        </DropdownMenuCheckboxItem>
                      ))}
                      
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Efficiency Metrics</DropdownMenuLabel>
                      {ALL_METRICS.filter(m => m.category === 'efficiency').map(metric => (
                        <DropdownMenuCheckboxItem
                          key={metric.id}
                          checked={visibleMetrics.includes(metric.id)}
                          onCheckedChange={() => toggleMetric(metric.id)}
                        >
                          <metric.icon className="h-4 w-4 mr-2" />
                          {metric.title}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {metricsToShow.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No metrics selected</p>
                    <p className="text-sm">Click &quot;Add Metrics&quot; to choose what to display</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {metricsToShow.map(metric => (
                      <KPICard
                        key={metric.id}
                        metricId={metric.id}
                        title={metric.title}
                        value={getMetricValue(metric.id)}
                        subtitle={getMetricSubtitle(metric.id)}
                        icon={metric.icon}
                        color={metric.color}
                        status={getMetricStatus(metric.id)}
                        onRemove={handleRemoveMetric}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        }
        
        return null;
      })}

      {/* Ad Spend Tracker - Campaign & Ad Level Tracking */}
      <AdSpendTracker 
        period={dateRange === 'last30' ? 'mtd' : dateRange === 'last7' ? 'week' : 'mtd'}
      />

      {/* Ad Performance Summary Section - Links to full Ads Manager */}
      <AdsSummaryCard dateRange={dateRange} />

      {/* Custom Reports Dashboard */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">Custom Reports</h2>
            {reports.length > 0 && (
              <Badge variant="outline">{reports.length} reports</Badge>
            )}
          </div>
          <Button
            onClick={() => {
              setEditingReport(null);
              setShowReportBuilder(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-report-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Report
          </Button>
        </div>
        
        {/* Compact Filter Bar for Custom Reports */}
        <Card className="p-3 bg-muted/30">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Quick Filters</span>
            </div>
            
            {/* Date Range */}
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-[140px] h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DATE_RANGES.map(range => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Event Type Filter */}
            <Select value={selectedConfiguredName} onValueChange={setSelectedConfiguredName}>
              <SelectTrigger className="w-[160px] h-7 text-xs">
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                {filters?.configured_names?.map(name => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Group By */}
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[130px] h-7 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="configured_name">Event Type</SelectItem>
                <SelectItem value="event_name">Raw Event</SelectItem>
                <SelectItem value="date">Date</SelectItem>
              </SelectContent>
            </Select>

            {/* Apply button */}
            <Button 
              size="sm" 
              variant="secondary"
              className="h-7 text-xs"
              onClick={() => { fetchOverview(); fetchBreakdown(); }}
              data-testid="apply-filters-reports-btn"
            >
              Apply
            </Button>
          </div>
        </Card>
        
        {reports.length > 0 ? (
          <MarketingDashboardGrid
            reports={reports}
            onEditReport={handleEditReport}
            onDuplicateReport={handleDuplicateReport}
            onReportDeleted={() => {
              fetchReports();
              setDashboardRefreshKey(prev => prev + 1);
            }}
            refreshKey={dashboardRefreshKey}
            dateRangeFilter={mapDateRangeForReports(dateRange)}
          />
        ) : (
          <Card className="p-8 text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No custom reports yet</p>
            <p className="text-sm text-muted-foreground mt-1">Click &quot;Add Report&quot; to create your first report</p>
          </Card>
        )}
      </div>

      {/* Data Source Info */}
      <div className="text-center text-xs text-muted-foreground">
        <p>
          Data synced from Cometly • Last updated: {lastSyncFormatted} • 
          <span className="font-medium"> {status?.total_events?.toLocaleString() || 0}</span> total events tracked
        </p>
      </div>
    </div>
    
    {/* Report Builder Modal */}
    <ReportBuilderModal
      isOpen={showReportBuilder}
      onClose={() => {
        setShowReportBuilder(false);
        setEditingReport(null);
      }}
      onReportCreated={handleReportCreated}
      editingReport={editingReport}
    />
    </Layout>
  );
};

export default MarketingTracker;
